// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  ErrorResponse,
  LayerBatchSync,
  LayerBatchSyncResponse,
  LayerListResponse,
  LayerReorder,
  PatchedStorySlideLayerUpdate,
  StoriesSlidesLayersFromAssetCreateBodyOne,
  StoriesSlidesLayersFromAssetCreateBodyThree,
  StorySlideLayer,
  StorySlideLayerCreate,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Get all layers on this slide ordered by z_index
 * @summary List slide layers
 */
export const storiesSlidesLayersRetrieve = (
  storyId: number,
  slideNumber: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<LayerListResponse>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getStoriesSlidesLayersRetrieveQueryKey = (
  storyId: number,
  slideNumber: number
) => {
  return [`/api/stories/${storyId}/slides/${slideNumber}/layers/`] as const;
};

export const getStoriesSlidesLayersRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getStoriesSlidesLayersRetrieveQueryKey(storyId, slideNumber);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>
  > = ({ signal }) =>
    storiesSlidesLayersRetrieve(storyId, slideNumber, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(storyId && slideNumber),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StoriesSlidesLayersRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>
>;
export type StoriesSlidesLayersRetrieveQueryError = ErrorResponse;

export function useStoriesSlidesLayersRetrieve<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
          TError,
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesSlidesLayersRetrieve<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
          TError,
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesSlidesLayersRetrieve<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List slide layers
 */

export function useStoriesSlidesLayersRetrieve<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getStoriesSlidesLayersRetrieveQueryOptions(
    storyId,
    slideNumber,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new layer on this slide (image, text, or shape)
 * @summary Create layer
 */
export const storiesSlidesLayersCreate = (
  storyId: number,
  slideNumber: number,
  storySlideLayerCreate: StorySlideLayerCreate,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StorySlideLayer>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: storySlideLayerCreate,
      signal,
    },
    options
  );
};

export const getStoriesSlidesLayersCreateMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersCreate>>,
    TError,
    { storyId: number; slideNumber: number; data: StorySlideLayerCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesSlidesLayersCreate>>,
  TError,
  { storyId: number; slideNumber: number; data: StorySlideLayerCreate },
  TContext
> => {
  const mutationKey = ["storiesSlidesLayersCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersCreate>>,
    { storyId: number; slideNumber: number; data: StorySlideLayerCreate }
  > = (props) => {
    const { storyId, slideNumber, data } = props ?? {};

    return storiesSlidesLayersCreate(
      storyId,
      slideNumber,
      data,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesSlidesLayersCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersCreate>>
>;
export type StoriesSlidesLayersCreateMutationBody = StorySlideLayerCreate;
export type StoriesSlidesLayersCreateMutationError = ErrorResponse;

/**
 * @summary Create layer
 */
export const useStoriesSlidesLayersCreate = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersCreate>>,
    TError,
    { storyId: number; slideNumber: number; data: StorySlideLayerCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesSlidesLayersCreate>>,
  TError,
  { storyId: number; slideNumber: number; data: StorySlideLayerCreate },
  TContext
> => {
  const mutationOptions = getStoriesSlidesLayersCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Get details of a specific layer
 * @summary Get layer
 */
export const storiesSlidesLayersRetrieve2 = (
  storyId: number,
  slideNumber: number,
  layerId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StorySlideLayer>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/${layerId}/`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getStoriesSlidesLayersRetrieve2QueryKey = (
  storyId: number,
  slideNumber: number,
  layerId: number
) => {
  return [
    `/api/stories/${storyId}/slides/${slideNumber}/layers/${layerId}/`,
  ] as const;
};

export const getStoriesSlidesLayersRetrieve2QueryOptions = <
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  layerId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getStoriesSlidesLayersRetrieve2QueryKey(storyId, slideNumber, layerId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>
  > = ({ signal }) =>
    storiesSlidesLayersRetrieve2(
      storyId,
      slideNumber,
      layerId,
      requestOptions,
      signal
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(storyId && slideNumber && layerId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StoriesSlidesLayersRetrieve2QueryResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>
>;
export type StoriesSlidesLayersRetrieve2QueryError = ErrorResponse;

export function useStoriesSlidesLayersRetrieve2<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  layerId: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
          TError,
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesSlidesLayersRetrieve2<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  layerId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
          TError,
          Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStoriesSlidesLayersRetrieve2<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  layerId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get layer
 */

export function useStoriesSlidesLayersRetrieve2<
  TData = Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
  TError = ErrorResponse
>(
  storyId: number,
  slideNumber: number,
  layerId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storiesSlidesLayersRetrieve2>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getStoriesSlidesLayersRetrieve2QueryOptions(
    storyId,
    slideNumber,
    layerId,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update position, transform, or type-specific properties of a layer
 * @summary Update layer
 */
export const storiesSlidesLayersPartialUpdate = (
  storyId: number,
  slideNumber: number,
  layerId: number,
  patchedStorySlideLayerUpdate: PatchedStorySlideLayerUpdate,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<StorySlideLayer>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/${layerId}/`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: patchedStorySlideLayerUpdate,
    },
    options
  );
};

export const getStoriesSlidesLayersPartialUpdateMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersPartialUpdate>>,
    TError,
    {
      storyId: number;
      slideNumber: number;
      layerId: number;
      data: PatchedStorySlideLayerUpdate;
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesSlidesLayersPartialUpdate>>,
  TError,
  {
    storyId: number;
    slideNumber: number;
    layerId: number;
    data: PatchedStorySlideLayerUpdate;
  },
  TContext
> => {
  const mutationKey = ["storiesSlidesLayersPartialUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersPartialUpdate>>,
    {
      storyId: number;
      slideNumber: number;
      layerId: number;
      data: PatchedStorySlideLayerUpdate;
    }
  > = (props) => {
    const { storyId, slideNumber, layerId, data } = props ?? {};

    return storiesSlidesLayersPartialUpdate(
      storyId,
      slideNumber,
      layerId,
      data,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesSlidesLayersPartialUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersPartialUpdate>>
>;
export type StoriesSlidesLayersPartialUpdateMutationBody =
  PatchedStorySlideLayerUpdate;
export type StoriesSlidesLayersPartialUpdateMutationError = ErrorResponse;

/**
 * @summary Update layer
 */
export const useStoriesSlidesLayersPartialUpdate = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersPartialUpdate>>,
    TError,
    {
      storyId: number;
      slideNumber: number;
      layerId: number;
      data: PatchedStorySlideLayerUpdate;
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesSlidesLayersPartialUpdate>>,
  TError,
  {
    storyId: number;
    slideNumber: number;
    layerId: number;
    data: PatchedStorySlideLayerUpdate;
  },
  TContext
> => {
  const mutationOptions =
    getStoriesSlidesLayersPartialUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Remove a layer from this slide
 * @summary Delete layer
 */
export const storiesSlidesLayersDestroy = (
  storyId: number,
  slideNumber: number,
  layerId: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<void>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/${layerId}/`,
      method: "DELETE",
    },
    options
  );
};

export const getStoriesSlidesLayersDestroyMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersDestroy>>,
    TError,
    { storyId: number; slideNumber: number; layerId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesSlidesLayersDestroy>>,
  TError,
  { storyId: number; slideNumber: number; layerId: number },
  TContext
> => {
  const mutationKey = ["storiesSlidesLayersDestroy"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersDestroy>>,
    { storyId: number; slideNumber: number; layerId: number }
  > = (props) => {
    const { storyId, slideNumber, layerId } = props ?? {};

    return storiesSlidesLayersDestroy(
      storyId,
      slideNumber,
      layerId,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesSlidesLayersDestroyMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersDestroy>>
>;

export type StoriesSlidesLayersDestroyMutationError = ErrorResponse;

/**
 * @summary Delete layer
 */
export const useStoriesSlidesLayersDestroy = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersDestroy>>,
    TError,
    { storyId: number; slideNumber: number; layerId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesSlidesLayersDestroy>>,
  TError,
  { storyId: number; slideNumber: number; layerId: number },
  TContext
> => {
  const mutationOptions = getStoriesSlidesLayersDestroyMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Create an image layer from a StoryAsset in the story's asset pool
 * @summary Create layer from asset
 */
export const storiesSlidesLayersFromAssetCreate = (
  storyId: number,
  slideNumber: number,
  storiesSlidesLayersFromAssetCreateBody:
    | StoriesSlidesLayersFromAssetCreateBodyOne
    | unknown
    | StoriesSlidesLayersFromAssetCreateBodyThree,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StorySlideLayer>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/from-asset/`,
      method: "POST",
      data: storiesSlidesLayersFromAssetCreateBody,
      signal,
    },
    options
  );
};

export const getStoriesSlidesLayersFromAssetCreateMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersFromAssetCreate>>,
    TError,
    {
      storyId: number;
      slideNumber: number;
      data:
        | StoriesSlidesLayersFromAssetCreateBodyOne
        | unknown
        | StoriesSlidesLayersFromAssetCreateBodyThree;
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesSlidesLayersFromAssetCreate>>,
  TError,
  {
    storyId: number;
    slideNumber: number;
    data:
      | StoriesSlidesLayersFromAssetCreateBodyOne
      | unknown
      | StoriesSlidesLayersFromAssetCreateBodyThree;
  },
  TContext
> => {
  const mutationKey = ["storiesSlidesLayersFromAssetCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersFromAssetCreate>>,
    {
      storyId: number;
      slideNumber: number;
      data:
        | StoriesSlidesLayersFromAssetCreateBodyOne
        | unknown
        | StoriesSlidesLayersFromAssetCreateBodyThree;
    }
  > = (props) => {
    const { storyId, slideNumber, data } = props ?? {};

    return storiesSlidesLayersFromAssetCreate(
      storyId,
      slideNumber,
      data,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesSlidesLayersFromAssetCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersFromAssetCreate>>
>;
export type StoriesSlidesLayersFromAssetCreateMutationBody =
  | StoriesSlidesLayersFromAssetCreateBodyOne
  | unknown
  | StoriesSlidesLayersFromAssetCreateBodyThree;
export type StoriesSlidesLayersFromAssetCreateMutationError = ErrorResponse;

/**
 * @summary Create layer from asset
 */
export const useStoriesSlidesLayersFromAssetCreate = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersFromAssetCreate>>,
    TError,
    {
      storyId: number;
      slideNumber: number;
      data:
        | StoriesSlidesLayersFromAssetCreateBodyOne
        | unknown
        | StoriesSlidesLayersFromAssetCreateBodyThree;
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesSlidesLayersFromAssetCreate>>,
  TError,
  {
    storyId: number;
    slideNumber: number;
    data:
      | StoriesSlidesLayersFromAssetCreateBodyOne
      | unknown
      | StoriesSlidesLayersFromAssetCreateBodyThree;
  },
  TContext
> => {
  const mutationOptions =
    getStoriesSlidesLayersFromAssetCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Update z_index values for multiple layers at once
 * @summary Reorder layers
 */
export const storiesSlidesLayersReorderCreate = (
  storyId: number,
  slideNumber: number,
  layerReorder: LayerReorder,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<LayerListResponse>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/reorder/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: layerReorder,
      signal,
    },
    options
  );
};

export const getStoriesSlidesLayersReorderCreateMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersReorderCreate>>,
    TError,
    { storyId: number; slideNumber: number; data: LayerReorder },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesSlidesLayersReorderCreate>>,
  TError,
  { storyId: number; slideNumber: number; data: LayerReorder },
  TContext
> => {
  const mutationKey = ["storiesSlidesLayersReorderCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersReorderCreate>>,
    { storyId: number; slideNumber: number; data: LayerReorder }
  > = (props) => {
    const { storyId, slideNumber, data } = props ?? {};

    return storiesSlidesLayersReorderCreate(
      storyId,
      slideNumber,
      data,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesSlidesLayersReorderCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersReorderCreate>>
>;
export type StoriesSlidesLayersReorderCreateMutationBody = LayerReorder;
export type StoriesSlidesLayersReorderCreateMutationError = ErrorResponse;

/**
 * @summary Reorder layers
 */
export const useStoriesSlidesLayersReorderCreate = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersReorderCreate>>,
    TError,
    { storyId: number; slideNumber: number; data: LayerReorder },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesSlidesLayersReorderCreate>>,
  TError,
  { storyId: number; slideNumber: number; data: LayerReorder },
  TContext
> => {
  const mutationOptions =
    getStoriesSlidesLayersReorderCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Create, update, and delete multiple layers in a single atomic request. All operations succeed or none do.
 * @summary Batch sync layers
 */
export const storiesSlidesLayersSyncCreate = (
  storyId: number,
  slideNumber: number,
  layerBatchSync: LayerBatchSync,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<LayerBatchSyncResponse>(
    {
      url: `/api/stories/${storyId}/slides/${slideNumber}/layers/sync/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: layerBatchSync,
      signal,
    },
    options
  );
};

export const getStoriesSlidesLayersSyncCreateMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersSyncCreate>>,
    TError,
    { storyId: number; slideNumber: number; data: LayerBatchSync },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storiesSlidesLayersSyncCreate>>,
  TError,
  { storyId: number; slideNumber: number; data: LayerBatchSync },
  TContext
> => {
  const mutationKey = ["storiesSlidesLayersSyncCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storiesSlidesLayersSyncCreate>>,
    { storyId: number; slideNumber: number; data: LayerBatchSync }
  > = (props) => {
    const { storyId, slideNumber, data } = props ?? {};

    return storiesSlidesLayersSyncCreate(
      storyId,
      slideNumber,
      data,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type StoriesSlidesLayersSyncCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storiesSlidesLayersSyncCreate>>
>;
export type StoriesSlidesLayersSyncCreateMutationBody = LayerBatchSync;
export type StoriesSlidesLayersSyncCreateMutationError = ErrorResponse;

/**
 * @summary Batch sync layers
 */
export const useStoriesSlidesLayersSyncCreate = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storiesSlidesLayersSyncCreate>>,
    TError,
    { storyId: number; slideNumber: number; data: LayerBatchSync },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storiesSlidesLayersSyncCreate>>,
  TError,
  { storyId: number; slideNumber: number; data: LayerBatchSync },
  TContext
> => {
  const mutationOptions =
    getStoriesSlidesLayersSyncCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
